import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MissingDogWall.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// Number of placeholder images available (update this when you add placeholder images)
const PLACEHOLDER_COUNT = 10;

function MissingDogWall() {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalDogs, setTotalDogs] = useState(0);
  const [perPage] = useState(60);
  const [trackFilter, setTrackFilter] = useState('');
  const [trainerFilter, setTrainerFilter] = useState('');
  const [lastRaceFrom, setLastRaceFrom] = useState('');
  const [lastRaceTo, setLastRaceTo] = useState('');
  const [filterOptions, setFilterOptions] = useState({ trainers: [], tracks: [] });
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [selectedDog, setSelectedDog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [placeholderMap, setPlaceholderMap] = useState({});
  const [wallCounters, setWallCounters] = useState(null);
  const [reportDog, setReportDog] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ reporter_name: '', reporter_contact: '', report_type: 'adopted', details: '' });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [incidentTooltip, setIncidentTooltip] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    fetchMissingDogs();
  }, [searchTerm, trackFilter, trainerFilter, lastRaceFrom, lastRaceTo, page]);

  // Any filter change resets to page 1 so the user doesn't end up on a page
  // that no longer exists in the filtered result set.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, trackFilter, trainerFilter, lastRaceFrom, lastRaceTo]);

  useEffect(() => {
    const fetchWallCounters = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/wall-counters`);
        setWallCounters(response.data || { fallen_this_year: 0, not_seen_month: 0 });
      } catch (error) {
        console.error('Error fetching wall counters:', error);
        setWallCounters({ fallen_this_year: 0, not_seen_month: 0 });
      }
    };
    fetchWallCounters();
  }, []);

  // Auto-open the modal for a specific dog when arriving with a ?dog= query
  // param (used by social-share links from /share/dog/<name>).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dogParam = params.get('dog');
    if (!dogParam) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/missing-dogs`, {
          params: { search: dogParam, page: 1, per_page: 20 },
        });
        const list = response.data?.dogs || [];
        const match = list.find((d) => d.name === dogParam) || list[0];
        if (!cancelled && match) {
          setSelectedDog(match);
          setShowModal(true);
        }
      } catch (e) {
        console.error('Failed to load shared dog:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Trainers and tracks both come from the /api/missing-dogs response
  // (available_trainers / available_tracks) so the dropdowns only ever
  // include options that match the staff-enabled tracks AND have at least
  // one missing dog under the current filter set.

  const clearFilters = () => {
    setTrackFilter('');
    setTrainerFilter('');
    setLastRaceFrom('');
    setLastRaceTo('');
  };

  // Count of filters currently narrowing the list (excludes searchTerm since the
  // mobile bar shows search separately). Used to label the "Filters" button badge.
  const activeFilterCount = (
    (trackFilter ? 1 : 0) +
    (trainerFilter ? 1 : 0) +
    (lastRaceFrom ? 1 : 0) +
    (lastRaceTo ? 1 : 0)
  );

  const fetchMissingDogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { search: searchTerm, page, per_page: perPage };
      if (trackFilter) params.track = trackFilter;
      if (trainerFilter) params.trainer = trainerFilter;
      if (lastRaceFrom) params.last_race_from = lastRaceFrom;
      if (lastRaceTo) params.last_race_to = lastRaceTo;
      const response = await axios.get(`${API_BASE}/api/missing-dogs`, { params });
      setDogs(response.data.dogs || []);
      setTotalDogs(response.data.total || 0);
      if (Array.isArray(response.data.available_trainers)) {
        setFilterOptions((prev) => ({
          ...prev,
          trainers: response.data.available_trainers,
        }));
      }
      if (Array.isArray(response.data.available_tracks)) {
        setFilterOptions((prev) => ({
          ...prev,
          tracks: response.data.available_tracks,
        }));
      }
    } catch (error) {
      console.error('Error fetching missing dogs:', error);
      setError('Failed to load missing dogs. Please try again later.');
      setDogs([]);
      setTotalDogs(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalDogs / perPage));
  const showingFrom = totalDogs === 0 ? 0 : (page - 1) * perPage + 1;
  const showingTo = Math.min(page * perPage, totalDogs);

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    try {
      return new Date(date).toLocaleDateString('en-GB');
    } catch {
      return 'Unknown';
    }
  };

  const calculateDaysNotSeen = (lastRaceDate) => {
    if (!lastRaceDate) return null;
    try {
      const lastDate = new Date(lastRaceDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      lastDate.setHours(0, 0, 0, 0);
      const diffTime = today - lastDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 ? diffDays : 0;
    } catch {
      return null;
    }
  };

  const getPlaceholderImage = (dogName) => {
    if (placeholderMap[dogName]) {
      return placeholderMap[dogName];
    }
    const randomNum = Math.floor(Math.random() * PLACEHOLDER_COUNT) + 1;
    const placeholder = `/placeholders/dog-placeholder-${randomNum}.jpg`;
    setPlaceholderMap(prev => ({ ...prev, [dogName]: placeholder }));
    return placeholder;
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const isDirectVideoUrl = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  };

  const getDogImage = (dog) => {
    if (dog.photo_url) {
      return dog.photo_url;
    }
    return getPlaceholderImage(dog.name);
  };

  const isPlaceholderImage = (dog) => {
    return !dog.photo_url;
  };

  const handleImageError = (e, dogName) => {
    const placeholder = getPlaceholderImage(dogName);
    if (e.target.src !== placeholder) {
      e.target.src = placeholder;
    } else {
      e.target.style.display = 'none';
      e.target.parentElement.style.background = '#f8f9fa';
      e.target.parentElement.style.display = 'flex';
      e.target.parentElement.style.alignItems = 'center';
      e.target.parentElement.style.justifyContent = 'center';
      if (!e.target.parentElement.querySelector('.no-image-text')) {
        const noImageText = document.createElement('div');
        noImageText.className = 'no-image-text';
        noImageText.textContent = 'No Photo Available';
        noImageText.style.cssText = 'font-size: 18px; font-weight: 700; color: #666; text-transform: uppercase;';
        e.target.parentElement.appendChild(noImageText);
      }
    }
  };

  const handleDogClick = (dog) => {
    setSelectedDog(dog);
    setShowModal(true);
  };

  const handleReportFound = async (e) => {
    e.preventDefault();
    setReportSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/found-reports`, {
        dog_name: reportDog.name,
        ...reportForm
      });
      setReportSuccess(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportDog(null);
        setReportSuccess(false);
        setReportForm({ reporter_name: '', reporter_contact: '', report_type: 'adopted', details: '' });
      }, 3000);
    } catch (error) {
      alert('Failed to submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleOpenReport = (e, dog) => {
    e.stopPropagation();
    setReportDog(dog);
    setShowReportModal(true);
  };

  const handleCloseReportModal = () => {
    setShowReportModal(false);
    setReportDog(null);
    setReportSuccess(false);
    setReportForm({ reporter_name: '', reporter_contact: '', report_type: 'adopted', details: '' });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDog(null);
  };

  const isDeceased = (dog) => {
    const s = (dog.status || '').toLowerCase();
    return s === 'deceased' || s === 'killed';
  };

  const isFallen = (dog) => {
    return (dog.status || '').toLowerCase() === 'fallen';
  };

  const posterHeader = (dog) => {
    if (isDeceased(dog)) return 'IN MEMORIAM';
    if (isFallen(dog)) return 'FALLEN';
    return 'MISSING';
  };

  const modalHeader = (dog) => {
    if (isDeceased(dog)) return 'In Memoriam';
    if (isFallen(dog)) return 'Fallen Dog Details';
    return 'Missing Dog Details';
  };

  const getShareUrl = (dogName) => {
    return `${window.location.origin}/share/dog/${encodeURIComponent(dogName)}`;
  };

  const shareOnFacebook = (e, dog) => {
    e.stopPropagation();
    const url = getShareUrl(dog.name);
    const text = `Have you seen ${dog.name}? This greyhound has been missing from racing. Help us find them!`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = (e, dog) => {
    e.stopPropagation();
    const url = getShareUrl(dog.name);
    const text = `Have you seen ${dog.name}? This greyhound has been missing from racing. Help us find them!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = (e, dog) => {
    e.stopPropagation();
    const url = getShareUrl(dog.name);
    const text = `Have you seen ${dog.name}? This greyhound has been missing from racing. Help us find them! ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const copyShareLink = (e, dog) => {
    e.stopPropagation();
    const url = getShareUrl(dog.name);
    navigator.clipboard.writeText(url).then(() => {
      const btn = e.currentTarget;
      const original = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 2000);
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/contact`, contactForm);
      setContactSuccess(true);
      setTimeout(() => {
        setShowContactModal(false);
        setContactSuccess(false);
        setContactForm({ name: '', email: '', subject: '', message: '' });
      }, 3000);
    } catch {
      alert('Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  // Random slight rotation for poster effect
  const getCardRotation = (index) => {
    const rotations = [-2, 1.5, -1, 2, -1.5, 0.5, -0.5, 1, -2.5, 1.8];
    return rotations[index % rotations.length];
  };

  // Trainer dropdown options: the dynamic "trainers with matching dogs" list from
  // the backend, with the currently-selected trainer spliced in if filters have
  // narrowed them out (so the user still sees their own selection).
  const trainerOptions = (() => {
    const base = filterOptions.trainers || [];
    if (trainerFilter && !base.includes(trainerFilter)) {
      return [trainerFilter, ...base].sort((a, b) => a.localeCompare(b));
    }
    return base;
  })();

  return (
    <div className="missing-wall">
      <a
        href="https://www.thesecretlivesofgreyhounds.org/"
        target="_blank"
        rel="noopener noreferrer"
        className="march-banner"
      >
        <span className="march-banner-text">
          New documentary, The Secret Lives of Greyhounds, coming soon. Watch the trailer <span className="march-banner-cta">here</span>
        </span>
      </a>
      <header className="missing-header">
        <div className="container">
          <div className="title-container">
            <div className="header-grid">
              <a
                href="https://www.youtube.com/@ShutDown.Campaigns/playlists"
                target="_blank"
                rel="noopener noreferrer"
                className="wall-counter-card header-counter-left"
              >
                <span className="counter-number">{wallCounters?.fallen_this_year ?? '—'}</span>
                <span className="counter-label">dogs fallen on tracks<br/>so far this year</span>
              </a>
              <h1 className="missing-title">
                GREYHOUND WATCH ENGLAND
              </h1>
              <div className="wall-counter-card header-counter-right">
                <span className="counter-number">{wallCounters?.not_seen_month ?? '—'}</span>
                <span className="counter-label">Dogs who raced this year not seen for over one month</span>
              </div>
            </div>
            <div className="title-subtitle">
              <p className="missing-subtitle concern-sentence">
                1 greyhound is killed on UK dog tracks every 3 days. Thousands of dogs disappear from tracks every year — they are either killed, returned to kennels, or rehomed. The dogs below are not currently racing — some following falls and potential injuries — but where have they gone? Please get in touch with any information.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="missing-main">
        <div className="container">
          {/* Desktop filter bar — hidden on mobile via CSS. */}
          <div className="wall-filter-bar wall-filter-bar-desktop">
            <div className="filter-row">
              <label>Search:</label>
              <input
                type="text"
                className="filter-search-input"
                placeholder="Search dog name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <label>Track:</label>
              <select value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}>
                <option value="">All tracks</option>
                {filterOptions.tracks.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <label>Trainer:</label>
              <select value={trainerFilter} onChange={(e) => setTrainerFilter(e.target.value)}>
                <option value="">All trainers</option>
                {trainerOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <label>Last race from:</label>
              <input type="date" value={lastRaceFrom} onChange={(e) => setLastRaceFrom(e.target.value)} />
              <label>to:</label>
              <input type="date" value={lastRaceTo} onChange={(e) => setLastRaceTo(e.target.value)} />
              <button className="filter-clear-btn" onClick={clearFilters}>Clear</button>
            </div>
          </div>

          {/* Mobile collapsed bar — hidden on desktop via CSS. */}
          <div className="wall-filter-bar-mobile">
            <input
              type="text"
              className="filter-search-input mobile-search"
              placeholder="Search dog name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className="filter-toggle-btn"
              onClick={() => setShowFilterSheet(true)}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-toggle-count">{activeFilterCount}</span>
              )}
            </button>
          </div>

          {loading && dogs.length === 0 ? (
            <div className="loading">Loading missing dogs...</div>
          ) : error ? (
            <div className="error-message">
              <h3>Error</h3>
              <p>{error}</p>
            </div>
              ) : (
                <div className={`missing-dogs-grid ${loading ? 'grid-refreshing' : ''}`}>
                  {dogs.length === 0 ? (
                    <div className="no-dogs">
                      {(searchTerm || trackFilter || trainerFilter || lastRaceFrom || lastRaceTo) ? (
                        <>
                          <h3>No Dogs Match Your Filters</h3>
                          <p>Try adjusting or clearing your filter criteria.</p>
                        </>
                      ) : (
                        <>
                          <h3>No Missing Dogs Currently</h3>
                          <p>All dogs are accounted for. Check back regularly for updates.</p>
                        </>
                      )}
                    </div>
                  ) : (
                     dogs.map((dog, index) => (
                       <div
                         key={dog.name}
                         className={`missing-dog-card poster ${isDeceased(dog) ? 'deceased-card' : ''} ${isFallen(dog) ? 'fallen-card' : ''}`}
                         onClick={() => handleDogClick(dog)}
                         style={{
                           cursor: 'pointer',
                           '--card-rotation': `${getCardRotation(index)}deg`
                         }}
                       >
                         <div className="poster-pin"></div>
                         <div className="poster-tape poster-tape-left"></div>
                         <div className="poster-tape poster-tape-right"></div>
                         <div className="poster-header">
                           {posterHeader(dog)}
                         </div>
                         {incidentTooltip === dog.name && (
                           <div className="incident-tooltip">Visible incident or injury in their last race</div>
                         )}
                         <div className="dog-photo-container">
                           <div className="card-badge-strip">
                             {dog.last_race_incident && (
                               <span
                                 className="card-badge card-badge-incident"
                                 onMouseEnter={(e) => { e.stopPropagation(); setIncidentTooltip(dog.name); }}
                                 onMouseLeave={() => setIncidentTooltip(null)}
                               >Incident</span>
                             )}
                             {typeof dog.weeks_unseen === 'number' && dog.weeks_unseen >= 2 && (
                               <span className="card-badge card-badge-weeks">{dog.weeks_unseen} wks unseen</span>
                             )}
                             {dog.latest_track && (
                               <span className="card-badge card-badge-track">{dog.latest_track}</span>
                             )}
                           </div>
                           <img src={getDogImage(dog)} alt={dog.name} onError={(e) => handleImageError(e, dog.name)} />
                           {isDeceased(dog) && (
                             <div className="deceased-overlay">
                               <div className="gravestone-icon">
                                 <svg viewBox="0 0 64 80" fill="currentColor" width="48" height="60">
                                   <path d="M12 80h40v-4H12v4zm2-8h36V40c0-9.94-8.06-18-18-18s-18 8.06-18 18v32zm18-44c2.21 0 4 1.79 4 4v4h-8v-4c0-2.21 1.79-4 4-4zm-6 14h12v2H26v-2zm-4 6h20v2H22v-2zm0 6h20v2H22v-2z"/>
                                 </svg>
                               </div>
                               <span className="deceased-label">R.I.P.</span>
                             </div>
                           )}
                         </div>
                        <div className="dog-info">
                          <h3 className="dog-name">{dog.name}</h3>
                          <div className="share-buttons">
                            <button className="share-btn share-facebook" onClick={(e) => shareOnFacebook(e, dog)} title="Share on Facebook">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                            </button>
                            <button className="share-btn share-twitter" onClick={(e) => shareOnTwitter(e, dog)} title="Share on X/Twitter">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                            </button>
                            <button className="share-btn share-whatsapp" onClick={(e) => shareOnWhatsApp(e, dog)} title="Share on WhatsApp">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </button>
                            <button className="share-btn share-copy" onClick={(e) => copyShareLink(e, dog)} title="Copy link">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            </button>
                          </div>
                          <div className="click-hint">Click for more details</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {!loading && !error && totalDogs > 0 && totalPages > 1 && (
                <div className="wall-pagination">
                  <button
                    type="button"
                    className="wall-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← Prev
                  </button>
                  <span className="wall-page-indicator">
                    Showing {showingFrom}–{showingTo} of {totalDogs} · Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="wall-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
        </main>

      {showModal && selectedDog && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalHeader(selectedDog)}: {selectedDog.name}</h2>
                <button className="close-button" onClick={handleCloseModal}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="modal-photo">
                  {getYouTubeId(selectedDog.video_url) ? (
                    <div className="video-container">
                      <iframe
                        src={`https://www.youtube.com/embed/${getYouTubeId(selectedDog.video_url)}`}
                        title="Race clip"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : isDirectVideoUrl(selectedDog.video_url) ? (
                    <div className="video-container">
                      <video controls src={selectedDog.video_url} style={{ width: '100%' }}>
                        Your browser does not support video playback.
                      </video>
                    </div>
                  ) : (
                    <img src={getDogImage(selectedDog)} alt={selectedDog.name} onError={(e) => handleImageError(e, selectedDog.name)} />
                  )}
                  {isDeceased(selectedDog) && (
                    <div className="deceased-overlay-modal">
                      <div className="gravestone-icon">
                        <svg viewBox="0 0 64 80" fill="currentColor" width="56" height="70">
                          <path d="M12 80h40v-4H12v4zm2-8h36V40c0-9.94-8.06-18-18-18s-18 8.06-18 18v32zm18-44c2.21 0 4 1.79 4 4v4h-8v-4c0-2.21 1.79-4 4-4zm-6 14h12v2H26v-2zm-4 6h20v2H22v-2zm0 6h20v2H22v-2z"/>
                        </svg>
                      </div>
                      <span className="deceased-label-modal">R.I.P.</span>
                    </div>
                  )}
                </div>

                {selectedDog.video_url && !getYouTubeId(selectedDog.video_url) && !isDirectVideoUrl(selectedDog.video_url) && (
                  <div className="modal-video-section">
                    <div className="video-link-container">
                      <a href={selectedDog.video_url} target="_blank" rel="noopener noreferrer" className="video-link">
                        Watch Race Clip
                      </a>
                    </div>
                  </div>
                )}

                <div className="modal-info-simple">
                  <div className="info-row">
                    <span className="info-label">Name:</span>
                    <span className="info-value">{selectedDog.name}</span>
                  </div>
                  {isDeceased(selectedDog) && (
                    <div className="info-row deceased-row">
                      <span className="info-label">Status:</span>
                      <span className="info-value">Deceased</span>
                    </div>
                  )}
                  {isFallen(selectedDog) && (
                    <div className="info-row fallen-row">
                      <span className="info-label">Status:</span>
                      <span className="info-value">Fallen</span>
                    </div>
                  )}
                  {selectedDog.age > 0 && (
                    <div className="info-row">
                      <span className="info-label">Age:</span>
                      <span className="info-value">{selectedDog.age} years</span>
                    </div>
                  )}
                  {selectedDog.sex && (
                    <div className="info-row">
                      <span className="info-label">Sex:</span>
                      <span className="info-value">{selectedDog.sex.toUpperCase() === 'D' ? 'Male' : selectedDog.sex.toUpperCase() === 'B' ? 'Female' : selectedDog.sex}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-label">Last Race Date:</span>
                    <span className="info-value">{formatDate(selectedDog.last_race_date)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Last Track:</span>
                    <span className="info-value">{selectedDog.latest_track || 'Unknown'}</span>
                  </div>
                  {selectedDog.trainer && selectedDog.trainer.name && (
                    <div className="info-row">
                      <span className="info-label">Trainer:</span>
                      <span className="info-value">{selectedDog.trainer.name}</span>
                    </div>
                  )}
                  {(() => {
                    const daysNotSeen = calculateDaysNotSeen(selectedDog.last_race_date);
                    if (daysNotSeen === null) return null;
                    const useWeeks = daysNotSeen >= 14;
                    const weeks = Math.floor(daysNotSeen / 7);
                    return (
                      <div className="info-row highlight">
                        <span className="info-label">Not seen for:</span>
                        <span className="info-value">
                          {useWeeks
                            ? `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`
                            : `${daysNotSeen} ${daysNotSeen === 1 ? 'day' : 'days'}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                <div className="modal-share-section">
                  <h4>Share this dog</h4>
                  <div className="modal-share-buttons">
                    <button className="modal-share-btn share-facebook" onClick={(e) => shareOnFacebook(e, selectedDog)}>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Facebook
                    </button>
                    <button className="modal-share-btn share-twitter" onClick={(e) => shareOnTwitter(e, selectedDog)}>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      X / Twitter
                    </button>
                    <button className="modal-share-btn share-whatsapp" onClick={(e) => shareOnWhatsApp(e, selectedDog)}>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                    <button className="modal-share-btn share-copy" onClick={(e) => copyShareLink(e, selectedDog)}>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      Copy Link
                    </button>
                  </div>
                </div>

                {!isDeceased(selectedDog) && (
                  <div className="modal-report-section">
                    <button className="report-found-btn" onClick={(e) => { handleCloseModal(); handleOpenReport(e, selectedDog); }}>
                      Report This Dog as Found
                    </button>
                  </div>
                )}

                <div className="modal-faq-link">
                  <a
                    href="https://www.animalaid.org.uk/new-greyhound-watch-website-faq/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    FAQ&nbsp;↗
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

      {showReportModal && reportDog && (
        <div className="modal-overlay report-modal-overlay" onClick={handleCloseReportModal}>
          <div className="report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h2>Report Found: {reportDog.name}</h2>
              <button className="close-button" onClick={handleCloseReportModal}>&times;</button>
            </div>
            <div className="report-modal-body">
              {reportSuccess ? (
                <div className="report-success">
                  Thank you! Your report has been submitted and will be reviewed by our team.
                </div>
              ) : (
                <form onSubmit={handleReportFound} className="report-found-form">
                  <p className="report-intro">Let us know about <strong>{reportDog.name}</strong>. Your report will be reviewed by our team.</p>
                  <div className="report-field">
                    <label>How was the dog found?</label>
                    <select value={reportForm.report_type} onChange={(e) => setReportForm({...reportForm, report_type: e.target.value})}>
                      <option value="adopted">Adopted / Rehomed</option>
                      <option value="raced_again">Raced Again</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="report-field">
                    <label>Your Name (optional)</label>
                    <input type="text" value={reportForm.reporter_name} onChange={(e) => setReportForm({...reportForm, reporter_name: e.target.value})} placeholder="Your name" />
                  </div>
                  <div className="report-field">
                    <label>Contact (optional)</label>
                    <input type="text" value={reportForm.reporter_contact} onChange={(e) => setReportForm({...reportForm, reporter_contact: e.target.value})} placeholder="Email or phone" />
                  </div>
                  <div className="report-field">
                    <label>Details</label>
                    <textarea value={reportForm.details} onChange={(e) => setReportForm({...reportForm, details: e.target.value})} placeholder="Any additional details about this dog..." rows="4" />
                  </div>
                  <div className="report-actions">
                    <button type="button" className="report-cancel-btn" onClick={handleCloseReportModal}>Cancel</button>
                    <button type="submit" className="report-submit-btn" disabled={reportSubmitting}>
                      {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <footer className="wall-footer">
        <div className="footer-content">
          <img src="/animalaid.png" alt="Animal Aid" className="footer-logo" />
          <p className="footer-text">
            Greyhound Watch UK is a collaboration between Animal Aid and Shut Down Campaigns. The information above has been compiled by a team of 30+ volunteer race monitors. If you have feedback about any of these dogs' records or wish to provide information, anonymously if necessary, about greyhound racing in England{' '}
            <button type="button" className="footer-contact-link" onClick={() => setShowContactModal(true)}>
              contact us here
            </button>.{' '}
            <a
              href="https://www.animalaid.org.uk/new-greyhound-watch-website-faq/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-contact-link"
            >
              Read our FAQ
            </a>.
          </p>
          <img src="/shutdown.png" alt="Shut Down Campaigns" className="footer-logo footer-logo-invert" />
        </div>
      </footer>

      {showContactModal && (
        <div className="modal-overlay contact-modal-overlay" onClick={() => { setShowContactModal(false); setContactSuccess(false); setContactForm({ name: '', email: '', subject: '', message: '' }); }}>
          <div className="report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h2>Contact Us</h2>
              <button className="close-button" onClick={() => { setShowContactModal(false); setContactSuccess(false); setContactForm({ name: '', email: '', subject: '', message: '' }); }}>&times;</button>
            </div>
            <div className="report-modal-body">
              {contactSuccess ? (
                <div className="report-success">
                  Thank you for your message. We will review it as soon as possible.
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="report-found-form">
                  <p className="report-intro">All fields except message are optional. You may submit anonymously.</p>
                  <div className="report-field">
                    <label>Your Name (optional)</label>
                    <input type="text" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} placeholder="Your name" />
                  </div>
                  <div className="report-field">
                    <label>Email (optional)</label>
                    <input type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} placeholder="Your email" />
                  </div>
                  <div className="report-field">
                    <label>Subject (optional)</label>
                    <input type="text" value={contactForm.subject} onChange={(e) => setContactForm({...contactForm, subject: e.target.value})} placeholder="Subject" />
                  </div>
                  <div className="report-field">
                    <label>Message *</label>
                    <textarea value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} placeholder="Your message..." rows="5" required />
                  </div>
                  <div className="report-actions">
                    <button type="button" className="report-cancel-btn" onClick={() => { setShowContactModal(false); setContactSuccess(false); setContactForm({ name: '', email: '', subject: '', message: '' }); }}>Cancel</button>
                    <button type="submit" className="report-submit-btn" disabled={contactSubmitting}>
                      {contactSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {showFilterSheet && (
        <div className="filter-sheet-overlay" onClick={() => setShowFilterSheet(false)}>
          <div className="filter-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <h3>Filters</h3>
              <button
                type="button"
                className="filter-sheet-close"
                onClick={() => setShowFilterSheet(false)}
                aria-label="Close"
              >&times;</button>
            </div>

            <div className="filter-sheet-body">
              <div className="sheet-field">
                <label>Track</label>
                <select value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}>
                  <option value="">All tracks</option>
                  {filterOptions.tracks.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="sheet-field">
                <label>Trainer</label>
                <select value={trainerFilter} onChange={(e) => setTrainerFilter(e.target.value)}>
                  <option value="">All trainers</option>
                  {trainerOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="sheet-field-row">
                <div className="sheet-field">
                  <label>Last race from</label>
                  <input type="date" value={lastRaceFrom} onChange={(e) => setLastRaceFrom(e.target.value)} />
                </div>
                <div className="sheet-field">
                  <label>To</label>
                  <input type="date" value={lastRaceTo} onChange={(e) => setLastRaceTo(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="filter-sheet-footer">
              <button
                type="button"
                className="sheet-clear-btn"
                onClick={() => { clearFilters(); }}
              >Clear all</button>
              <button
                type="button"
                className="sheet-apply-btn"
                onClick={() => setShowFilterSheet(false)}
              >Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MissingDogWall;
